import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { Mail, Phone, MapPin, Send, Briefcase, LifeBuoy, MessageCircle, Clock, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function Contact() {
  const [f, setF] = useState({ name:"", email:"", phone:"", subject:"", message:"" });
  const [busy, setBusy] = useState(false);
  const [c, setC] = useState(null);
  const [sent, setSent] = useState(false);
  const set = (k,v) => setF(s => ({...s, [k]:v}));

  useEffect(() => { api.get("/company").then(r => setC(r.data)).catch(()=>{}); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", f);
      toast.success("Message sent! We'll respond within 24 hours.");
      setSent(true);
      setF({ name:"", email:"", phone:"", subject:"", message:"" });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };

  const addr = c?.address;
  const fullAddress = addr ? `${addr.line1}, ${addr.line2}, ${addr.city}, ${addr.state} ${addr.postal}` : "Loading...";

  const inputCls = "w-full px-4 py-3 rounded-xl border border-gray-200 bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none transition text-sm";
  const labelCls = "text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5 block";

  return (
    <div className="bg-[#FBFAF6]" data-testid="contact-page">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-gray-200">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-50/70 via-[#FBFAF6] to-rose-50/40 pointer-events-none"/>
        <div className="relative max-w-7xl mx-auto px-5 md:px-10 py-16 md:py-20">
          <span className="inline-block px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 text-xs font-semibold uppercase tracking-wider">Get in touch</span>
          <h1 className="font-display font-black text-5xl md:text-7xl mt-4 tracking-tighter text-[#0E1F18]">Let&apos;s talk.</h1>
          <p className="mt-4 text-lg md:text-xl text-[#4A4A4A] max-w-2xl">Press, partnerships, support, or just a friendly hello — drop us a line and we&apos;ll get back within 24 hours.</p>
        </div>
      </section>

      {/* Main content */}
      <section className="max-w-7xl mx-auto px-5 md:px-10 py-16">
        <div className="grid lg:grid-cols-[1fr_420px] gap-8">
          {/* Form */}
          <form onSubmit={submit} className="bg-white rounded-3xl border border-gray-200 p-6 md:p-10 shadow-[0_8px_30px_rgb(0,0,0,0.04)] space-y-5" data-testid="contact-form">
            <div>
              <h2 className="font-display font-black text-3xl text-[#0E1F18]">Send us a message</h2>
              <p className="text-gray-500 text-sm mt-1">Fill in the form and our team will reach out shortly.</p>
            </div>

            {sent && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-start gap-3" data-testid="sent-banner">
                <CheckCircle2 size={20} className="text-emerald-600 flex-shrink-0 mt-0.5"/>
                <div>
                  <div className="font-bold text-emerald-800">Message received!</div>
                  <div className="text-sm text-emerald-700">We&apos;ll respond at the email you provided within 24 hours.</div>
                </div>
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Name</label>
                <input required className={inputCls} value={f.name} onChange={e=>set("name", e.target.value)} placeholder="Your full name" data-testid="contact-name"/>
              </div>
              <div>
                <label className={labelCls}>Email</label>
                <input required type="email" className={inputCls} value={f.email} onChange={e=>set("email", e.target.value)} placeholder="you@email.com" data-testid="contact-email"/>
              </div>
              <div>
                <label className={labelCls}>Phone (optional)</label>
                <input className={inputCls} value={f.phone} onChange={e=>set("phone", e.target.value)} placeholder="+91 9876543210" data-testid="contact-phone"/>
              </div>
              <div>
                <label className={labelCls}>Subject</label>
                <input required className={inputCls} value={f.subject} onChange={e=>set("subject", e.target.value)} placeholder="Partnership, support, press..." data-testid="contact-subject"/>
              </div>
            </div>
            <div>
              <label className={labelCls}>Message</label>
              <textarea required className={`${inputCls} min-h-[160px]`} value={f.message} onChange={e=>set("message", e.target.value)} placeholder="Tell us a bit more..." data-testid="contact-message"/>
            </div>
            <button disabled={busy} className="w-full inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white font-semibold py-3.5 rounded-xl transition" data-testid="contact-submit">
              <Send size={16}/> {busy ? "Sending..." : "Send message"}
            </button>
          </form>

          {/* Sidebar info */}
          <div className="space-y-4">
            {/* Primary contact card */}
            <div className="bg-gradient-to-br from-emerald-600 via-emerald-500 to-teal-500 rounded-3xl p-8 text-white shadow-lg">
              <div className="inline-block px-3 py-1 rounded-full bg-white/20 border border-white/30 text-xs font-semibold uppercase tracking-wider">Reach the team</div>
              <h3 className="font-display font-black text-2xl mt-4">We&apos;re here to help.</h3>
              <div className="mt-6 space-y-3.5">
                <a href={`mailto:${c?.contact?.emails?.primary || "hello@hostro.in"}`} className="group flex items-center gap-3 hover:bg-white/10 -mx-2 px-2 py-2 rounded-lg transition">
                  <div className="w-9 h-9 rounded-full bg-white/20 grid place-items-center"><Mail size={16}/></div>
                  <div>
                    <div className="font-semibold text-sm">{c?.contact?.emails?.primary || "hello@hostro.in"}</div>
                    <div className="text-xs text-white/70">General queries</div>
                  </div>
                </a>
                <a href={`mailto:${c?.contact?.emails?.support || "support@hostro.in"}`} className="group flex items-center gap-3 hover:bg-white/10 -mx-2 px-2 py-2 rounded-lg transition">
                  <div className="w-9 h-9 rounded-full bg-white/20 grid place-items-center"><LifeBuoy size={16}/></div>
                  <div>
                    <div className="font-semibold text-sm">{c?.contact?.emails?.support || "support@hostro.in"}</div>
                    <div className="text-xs text-white/70">Customer support</div>
                  </div>
                </a>
                <a href={`mailto:${c?.contact?.emails?.business || "business@hostro.in"}`} className="group flex items-center gap-3 hover:bg-white/10 -mx-2 px-2 py-2 rounded-lg transition">
                  <div className="w-9 h-9 rounded-full bg-white/20 grid place-items-center"><Briefcase size={16}/></div>
                  <div>
                    <div className="font-semibold text-sm">{c?.contact?.emails?.business || "business@hostro.in"}</div>
                    <div className="text-xs text-white/70">Partnerships</div>
                  </div>
                </a>
                <a href={`tel:${(c?.contact?.phone||"").replace(/\s/g,"")}`} className="group flex items-center gap-3 hover:bg-white/10 -mx-2 px-2 py-2 rounded-lg transition">
                  <div className="w-9 h-9 rounded-full bg-white/20 grid place-items-center"><Phone size={16}/></div>
                  <div>
                    <div className="font-semibold text-sm">{c?.contact?.phone || "+91 89052 90804"}</div>
                    <div className="text-xs text-white/70">Mon–Sat, 9 AM – 7 PM</div>
                  </div>
                </a>
                <a href="https://wa.me/918905290804?text=Hi%20Hostro!" target="_blank" rel="noreferrer" className="group flex items-center gap-3 hover:bg-white/10 -mx-2 px-2 py-2 rounded-lg transition">
                  <div className="w-9 h-9 rounded-full bg-white/20 grid place-items-center"><MessageCircle size={16}/></div>
                  <div>
                    <div className="font-semibold text-sm">WhatsApp us</div>
                    <div className="text-xs text-white/70">Reply within 30 mins</div>
                  </div>
                </a>
              </div>
            </div>

            {/* Office card */}
            <div className="bg-white rounded-3xl border border-gray-200 p-6">
              <div className="inline-block px-3 py-1 rounded-full bg-rose-100 text-rose-800 text-xs font-semibold uppercase tracking-wider">Visit us</div>
              <h3 className="font-display font-black text-xl mt-3 text-[#0E1F18]">Office</h3>
              <div className="mt-3 flex gap-3">
                <MapPin size={18} className="text-emerald-600 flex-shrink-0 mt-1"/>
                <div className="text-sm text-gray-600">
                  <div className="font-semibold text-[#0E1F18]">{c?.legal_name || "Hostro Ventures Private Limited"}</div>
                  <div>{fullAddress}</div>
                </div>
              </div>
              {c?.cin && (
                <div className="mt-3 pt-3 border-t border-gray-100 text-xs text-gray-500">
                  <div>CIN: <code className="text-gray-700">{c.cin}</code></div>
                  <div>GSTIN: <code className="text-gray-700">{c.gstin}</code></div>
                </div>
              )}
            </div>

            {/* Hours card */}
            <div className="bg-white rounded-3xl border border-gray-200 p-6 flex items-center gap-4" data-testid="hours-card">
              <div className="w-12 h-12 rounded-xl bg-amber-50 border border-amber-200 text-amber-700 grid place-items-center"><Clock size={20}/></div>
              <div>
                <div className="font-bold text-[#0E1F18]">Support hours</div>
                <div className="text-sm text-gray-500">Mon – Sat · 9 AM to 7 PM IST</div>
              </div>
            </div>
          </div>
        </div>

        {/* Map */}
        <div className="mt-10 rounded-3xl overflow-hidden border border-gray-200 shadow-sm">
          <iframe
            title="Hostro office on map"
            className="w-full h-[360px] block"
            src={`https://maps.google.com/maps?q=${encodeURIComponent(fullAddress)}&z=15&output=embed`}
            loading="lazy"
          />
        </div>
      </section>
    </div>
  );
}
